"use client";

import React, { useEffect, useState, useRef } from "react";
import { useClickOutsideHandler } from '@/hooks/useClickOutsideHandler';
import { useTheme } from 'next-themes';
import { useToast } from '@/components/ui/use-toast';
import { useSession } from '@/context/SessionContext';
import { useAppearance } from '@/context/AppearanceContext';
import { getProfileAppearance, updateProfileAppearance } from '@/lib/supabase';
import { Button } from "@/components/ui/button";
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Switch } from '@/components/ui/switch';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { X, Paintbrush, Sun, Moon, SunMoon } from '@/components/icons';
import { cn, offcanvasSidebarPanelClass } from '@/lib/utils';
import { resolveDefaultPrimaryHex } from '@/lib/appearanceDefaults';
import useHeaderOffset from '@/hooks/useHeaderOffset';
import { useScrollLock } from '@/hooks/useScrollLock';
import GOOGLE_FONTS from '@/data/googleFonts';
import { usePullToClose } from '@/hooks/usePullToClose';

type Appearance = {
  palette: string;
  // NOTE: 'font' property has been deprecated and moved to fontGovernance.ts
  // The app now uses Titillium Web exclusively
  customPrimary?: string;
  customAccent?: string;
  customForeground?: string;
  theme?: 'light' | 'dark' | 'auto';
};

// TIPOGRAFIA BLOQUEADA A TITILLIUM WEB - Ya no se permite seleccionar fuentes
// La aplicación usa exclusivamente Titillium Web
// (Se lee desde fontGovernance.ts)
const FONTS: Record<string, { name: string; family: string }> = {
  // Mantener constante solo como referencia (deprecated)
  titillium: { name: "Titillium Web (fijo)", family: "'Titillium Web', system-ui, -apple-system, 'Segoe UI', Roboto, 'Helvetica Neue', Arial" },
};

const PROPRIETARY_COLORS = [
  { id: 'obelisco', name: 'Obelisco', hex: '#334155' }, // default
  { id: 'andes', name: 'Andes', hex: '#8B7355' }, // dark sandy
  { id: 'azul-porteno', name: 'Azul Porteño', hex: '#2563eb' },
  { id: 'dulce-de-leche', name: 'Dulce de Leche', hex: '#f97316' },
  { id: 'estrella-de-mar', name: 'Estrella de Mar', hex: '#fb7185' },
  { id: 'iguazu', name: 'Iguazú', hex: '#10b981' },
  { id: 'jacaranda', name: 'Jacarandá', hex: '#a259ff' },
  { id: 'malbec', name: 'Malbec', hex: '#ef4444' },
  { id: 'mar-del-plata', name: 'Mar del Plata', hex: '#0ea5e9' },
  { id: 'pampa', name: 'Pampa', hex: '#16a34a' },
  { id: 'patagonia', name: 'Patagonia', hex: '#475569' },
  { id: 'rio-parana', name: 'Río Paraná', hex: '#06b6d4' },
  { id: 'sierra', name: 'Sierra', hex: '#0ea5b7' },
  { id: 'sol-de-mayo', name: 'Sol de Mayo', hex: '#f59e0b' },
];

function hexToHslString(hex: string): string {
  const r = parseInt(hex.slice(1, 3), 16) / 255;
  const g = parseInt(hex.slice(3, 5), 16) / 255;
  const b = parseInt(hex.slice(5, 7), 16) / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0;
  let s = 0;
  const l = (max + min) / 2;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break;
      case g: h = ((b - r) / d + 2) / 6; break;
      case b: h = ((r - g) / d + 4) / 6; break;
    }
  }
  return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
}

function applyAppearance(settings: Appearance) {
  if (typeof window === 'undefined') return;
  const root = document.documentElement;
  
  // No sobreescribir --foreground ni tipografía manualmente si ya están en globals.css
  // esto evita conflictos con el modo oscuro nativo
  
  if (settings.customPrimary) {
    try {
      const primary = hexToHslString(settings.customPrimary);
      root.style.setProperty('--primary', primary);
    } catch (e) {}
  }
  if (settings.customAccent) {
    try {
      const accent = hexToHslString(settings.customAccent);
      root.style.setProperty('--accent', accent);
    } catch (e) {}
  }

  // Si el usuario eligió un foreground personalizado, aplicarlo, 
  // de lo contrario dejar que globals.css maneje el foreground del tema
  if (settings.customForeground) {
    try {
      const fg = hexToHslString(settings.customForeground);
      root.style.setProperty('--foreground', fg);
    } catch (e) {}
  } else {
    // IMPORTANTE: Si NO hay customForeground, eliminar la propiedad inline 
    // para que prevalezca lo definido en globals.css (.dark vs :root)
    root.style.removeProperty('--foreground');
  }

  // Notify that appearance has been updated
  window.dispatchEvent(new CustomEvent('appearance-updated'));
}

export default function AppearanceSidebar() {
  const defaultPrimary = resolveDefaultPrimaryHex();
  const { isOpen, close } = useAppearance();
  const [isClosing, setIsClosing] = useState(false);
  const sidebarRef = useRef<HTMLDivElement>(null);
  const { headerHeight, isDesktop, bottomNavHeight, recalculate } = useHeaderOffset();

  const handleClose = React.useCallback(() => {
    setIsClosing(true);
    setTimeout(() => {
      close();
      setIsClosing(false);
    }, 200);
  }, [close]);

  // REINFORCED: Initial state for unauthenticated users starts with 'auto' theme and the CSS default color
  // This ensures first-time users see a proper theme matching their system preference
  const [settings, setSettings] = useState<Appearance>({ 
    palette: "custom", 
    font: "titillium",
    theme: "auto",
    customPrimary: defaultPrimary,
  });
  const settingsRef = useRef<Appearance>({
    palette: "custom",
    theme: "auto",
    customPrimary: defaultPrimary,
  });
  const [customPrimary, setCustomPrimary] = useState<string>(defaultPrimary);
  const [customAccent, setCustomAccent] = useState<string>("#c4b5fd");
  const { theme, setTheme } = useTheme();
  const [customForeground, setCustomForeground] = useState<string | undefined>(undefined);
  const { session, profile } = useSession();
  const { toast } = useToast();
  const [appearanceLoaded, setAppearanceLoaded] = useState(false);
  const [systemTheme, setSystemTheme] = useState<'light' | 'dark'>('light');

  // Pull-to-close: ref al ScrollArea
  const scrollAreaRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isOpen) {
      recalculate();
      const timeoutId = setTimeout(recalculate, 50);
      return () => clearTimeout(timeoutId);
    }
  }, [isOpen, recalculate]);

  // Click outside to close - usando hook reutilizable
  useClickOutsideHandler({
    isOpen,
    sidebarRef,
    handleClose,
  });

  useEffect(() => {
    const load = async () => {
      try {
        if (profile?.id) {
          // Usuario autenticado: cargar preferencias de Supabase
          const server = await getProfileAppearance(profile.id);
          if (server) {
            setSettings(server);
            if ((server as any).customPrimary) setCustomPrimary((server as any).customPrimary);
            if ((server as any).customAccent) setCustomAccent((server as any).customAccent);
            if ((server as any).customForeground) setCustomForeground((server as any).customForeground);
            // Sincronizar el tema global con el guardado en la configuración
            if ((server as any).theme) {
              setTheme((server as any).theme);
            }
            // IMPORTANT: Cache appearance in localStorage for early script to pick up on next reload
            // This prevents the "flash" of default color on next page load
            try {
              localStorage.setItem('appearance-settings', JSON.stringify(server));
            } catch (storageError) {
              // Fail silently if localStorage is not available
              console.warn('[AppearanceSidebar] Could not cache to localStorage:', storageError);
            }
            return;
          }
        } else {
          // Usuario no autenticado (guest): cargar preferencias del guest desde localStorage
          const guestAppearance = localStorage.getItem('guest-appearance-settings');
          if (guestAppearance) {
            try {
              const parsed = JSON.parse(guestAppearance);
              setSettings(parsed);
              if (parsed.customPrimary) setCustomPrimary(parsed.customPrimary);
              if (parsed.customAccent) setCustomAccent(parsed.customAccent);
              if (parsed.customForeground) setCustomForeground(parsed.customForeground);
              if (parsed.theme) {
                setTheme(parsed.theme);
              }
              return;
            } catch (e) {
              console.warn('[AppearanceSidebar] Error parsing guest appearance:', e);
            }
          }
        }
      } catch (e) {
        console.warn('[AppearanceSidebar] error loading appearance:', e);
      } finally {
        setAppearanceLoaded(true);
      }
    };
    setAppearanceLoaded(false);
    load();
  }, [profile?.id, setTheme]);

  // Apply appearance whenever settings change, regardless of sidebar state
  // This ensures guest preferences persist correctly
  useEffect(() => {
    if (!appearanceLoaded) return;
    if (typeof window !== "undefined") {
      applyAppearance(settings);
    }
  }, [settings, appearanceLoaded]);

  // Also re-apply on sidebar open to ensure consistency
  useEffect(() => {
    if (!isOpen) return;
    if (!appearanceLoaded) return;
    if (typeof window !== "undefined") {
      applyAppearance(settings);
    }
  }, [isOpen]);

  useEffect(() => {
    settingsRef.current = settings;
  }, [settings]);

  // Sincronizar el tema actual con el de settings cuando cambia
  useEffect(() => {
    if (!isOpen) return;
    if (theme && settings.theme !== theme) {
      setSettings(prev => ({ ...prev, theme: theme as 'light' | 'dark' | 'auto' }));
    }
  }, [theme, settings.theme, isOpen]);

  // Listen to system theme changes (when in auto mode)
  useEffect(() => {
    if (typeof window === 'undefined') return;
    
    const darkModeQuery = window.matchMedia('(prefers-color-scheme: dark)');
    
    const handleThemeChange = (e: MediaQueryListEvent) => {
      const newSystemTheme = e.matches ? 'dark' : 'light';
      setSystemTheme(newSystemTheme);
      
      // If current theme is 'auto', apply the system theme to the DOM
      if (theme === 'auto') {
        const htmlElement = document.documentElement;
        if (newSystemTheme === 'dark') {
          htmlElement.classList.add('dark');
        } else {
          htmlElement.classList.remove('dark');
        }
      }
    };
    
    // Set initial value
    const isDark = darkModeQuery.matches;
    setSystemTheme(isDark ? 'dark' : 'light');
    
    // Listen for changes
    darkModeQuery.addEventListener('change', handleThemeChange);
    
    return () => {
      darkModeQuery.removeEventListener('change', handleThemeChange);
    };
  }, [theme]);

  // When theme is set to 'auto', immediately apply the current system theme
  useEffect(() => {
    if (typeof window === 'undefined' || theme !== 'auto') return;
    
    // Use a small delay to ensure next-themes has finished processing
    const timeoutId = setTimeout(() => {
      const darkModeQuery = window.matchMedia('(prefers-color-scheme: dark)');
      const isDark = darkModeQuery.matches;
      const htmlElement = document.documentElement;
      
      if (isDark) {
        htmlElement.classList.add('dark');
      } else {
        htmlElement.classList.remove('dark');
      }
    }, 0);
    
    return () => clearTimeout(timeoutId);
  }, [theme]);

  // DEPRECATED: handleSelectFont removed - font is now governed by fontGovernance.ts

  const handleReset = () => {
    const def: Appearance = { palette: "neutral" };
    setSettings(def);
    applyAppearance(def);
  };

  // Activar pull-to-close (solo en móvil)
  usePullToClose(scrollAreaRef, {
    onClose: handleClose,
    threshold: 80,
    enabled: isOpen && !isDesktop
  });

  useScrollLock(isOpen || isClosing, false, scrollAreaRef);

  const saveSettings = async (s: Appearance) => {
    const payload: Appearance = {
      ...s,
      customPrimary: s.customPrimary,
      customAccent: s.customAccent,
      customForeground: s.customForeground,
      theme: s.theme ?? undefined,
      // NOTE: 'font' is no longer included - governed by fontGovernance.ts
    };

    try {
      const isAuthenticated = session?.user?.id && profile?.id;
      if (isAuthenticated) {
        // Usuario autenticado: guardar en Supabase
        const res = await updateProfileAppearance(profile.id, payload);
        
        // También guardar en localStorage como caché
        try {
          localStorage.setItem('appearance-settings', JSON.stringify(payload));
          // Emit custom event to notify MainLayout of appearance change
          window.dispatchEvent(new CustomEvent('appearance-settings-changed', { detail: { payload } }));
        } catch (storageError) {
          console.warn('[AppearanceSidebar] Could not cache to localStorage:', storageError);
        }
        
        return res;
      } else {
        // Usuario no autenticado (guest): guardar solo en localStorage
        try {
          localStorage.setItem('guest-appearance-settings', JSON.stringify(payload));
          console.debug('[AppearanceSidebar] Saved guest appearance to localStorage:', payload);
          // Emit custom event to notify MainLayout of appearance change
          window.dispatchEvent(new CustomEvent('appearance-settings-changed', { detail: { payload } }));
        } catch (storageError) {
          console.warn('[AppearanceSidebar] Could not save guest preferences to localStorage:', storageError);
        }
        return null;
      }
    } catch (err) {
      console.error('[AppearanceSidebar] Error saving settings:', err);
      return null;
    }
  };

  const handleSave = async () => {
    try {
      const res = await saveSettings(settings);
      applyAppearance(settings);
      handleClose();
    } catch (err) {
      console.error('[AppearanceSidebar] Save error:', err);
      toast({ title: 'Error', description: 'No se pudo guardar las preferencias.', variant: 'destructive' });
    }
  };

  return (
    <>
      {/* Overlay */}
      {(isOpen || isClosing) && (
        <div
          className={cn(
            'fixed inset-0 bg-transparent z-[9997] transition-opacity duration-200',
            isClosing && 'opacity-0 pointer-events-none'
          )}
          onClick={handleClose}
        />
      )}

      <div
        ref={sidebarRef}
        className={cn(
          'fixed right-0 w-full lg:w-[400px] bg-background z-[9998] flex flex-col transition-transform duration-200',
          offcanvasSidebarPanelClass(isOpen, isClosing)
        )}
        style={{
          top: isDesktop ? `${headerHeight}px` : '0',
          bottom: !isDesktop ? 0 : undefined,
          height: isDesktop ? `calc(100vh - ${headerHeight}px)` : '100dvh',
          pointerEvents: (isOpen && !isClosing) ? 'auto' : 'none'
        }}
      >
      {/* Safe Area Spacer for Mobile */}
      {!isDesktop && <div className="h-[env(safe-area-inset-top,0px)] bg-primary w-full shrink-0" />}
      <div className="flex items-center justify-between p-4 border-b bg-primary text-white min-h-[64px]">
        <h2 className="text-lg font-semibold flex items-center gap-2 text-white">
          <Paintbrush className="w-5 h-5 text-white" />
          Apariencia
        </h2>
        <div className="flex items-center gap-2">
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  onClick={(e) => { e.stopPropagation(); handleClose(); }}
                  className="p-1 hover:bg-white/10 text-white rounded-lg transition-colors hover:text-white"
                >
                  <X className="w-5 h-5 text-white" />
                </button>
              </TooltipTrigger>
              <TooltipContent variant="sidebar">
                <p>Cerrar</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      </div>

      <ScrollArea ref={scrollAreaRef} className="flex-1">
        <div className="p-4 space-y-6">
          <div>
            {/* <p className="text-sm text-muted-foreground mb-2">Elige una paleta y una combinación tipográfica para tu app.</p> */}
          </div>

          {/* Switch Tema: Light / Auto / Dark */}
          <div>
            <h3 className="text-sm font-medium mb-3 flex items-center gap-2">
              <span>Tema</span>
            </h3>
            <div className="flex flex-col gap-4 p-3 rounded-lg border">
              {/* Icon and Description */}
              <div className="flex items-center gap-3">
                {theme === 'auto' ? (
                  systemTheme === 'dark' ? (
                    <Moon className="h-4 w-4 text-muted-foreground" />
                  ) : (
                    <Sun className="h-4 w-4 text-muted-foreground" />
                  )
                ) : theme === 'dark' ? (
                  <Moon className="h-4 w-4 text-muted-foreground" />
                ) : (
                  <Sun className="h-4 w-4 text-muted-foreground" />
                )}
                <div className="flex flex-col">
                  <span className="text-sm font-medium">
                    {theme === 'dark' 
                      ? 'Tema oscuro' 
                      : theme === 'light' 
                      ? 'Tema claro'
                      : `Automático (${systemTheme === 'dark' ? 'oscuro' : 'claro'})`
                    }
                  </span>
                  <span className="text-xs text-muted-foreground">
                    {theme === 'dark' 
                      ? 'Interfaz con colores oscuros'
                      : theme === 'light'
                      ? 'Interfaz con colores claros'
                      : 'Se adapta al tema del sistema operativo'
                    }
                  </span>
                </div>
              </div>

              {/* Three-way Toggle */}
              <ToggleGroup
                type="single"
                value={theme || 'light'}
                onValueChange={(value) => {
                  if (value) {
                    const newTheme = value as 'light' | 'dark' | 'auto';
                    setTheme(newTheme);
                    const newSettings = { ...settingsRef.current, theme: newTheme };
                    settingsRef.current = newSettings;
                    setSettings(newSettings);
                    void saveSettings(newSettings);
                  }
                }}
                className="w-full [&_button[data-state=on]]:bg-primary [&_button[data-state=on]]:text-primary-foreground hover:[&_button[data-state=on]]:bg-primary/90"
              >
                <ToggleGroupItem 
                  value="light" 
                  className="flex-1 gap-2"
                  aria-label="Tema claro"
                >
                  <Sun className="h-4 w-4" />
                  <span className="text-xs sm:text-sm">Claro</span>
                </ToggleGroupItem>
                <ToggleGroupItem 
                  value="auto" 
                  className="flex-1 gap-2"
                  aria-label="Tema automático"
                >
                  <SunMoon className="h-4 w-4" />
                  <span className="text-xs sm:text-sm">Auto</span>
                </ToggleGroupItem>
                <ToggleGroupItem 
                  value="dark" 
                  className="flex-1 gap-2"
                  aria-label="Tema oscuro"
                >
                  <Moon className="h-4 w-4" />
                  <span className="text-xs sm:text-sm">Oscuro</span>
                </ToggleGroupItem>
              </ToggleGroup>
            </div>
          </div>

          <div>
            {/* <h3 className="text-sm font-medium mb-3">Colores</h3> */}
            <div className="flex gap-2 flex-wrap">
              {PROPRIETARY_COLORS.map((c) => (
                <button
                  key={c.id}
                  onClick={async () => {
                    setCustomPrimary(c.hex);
                    const newSettings = { ...settingsRef.current, palette: 'custom', customPrimary: c.hex } as Appearance;
                    settingsRef.current = newSettings;
                    setSettings(newSettings);
                    try { 
                      applyAppearance(newSettings);
                      await saveSettings(newSettings);
                    } catch (e) {
                      console.error('[AppearanceSidebar] Error saving color preference:', e);
                    }
                  }}
                  aria-label={c.name}
                  className="flex items-center gap-2 px-3 py-2 rounded-md border hover:bg-muted transition-colors"
                >
                  <span className="w-6 h-6 rounded-md border" style={{ background: c.hex }} aria-hidden />
                  <span className="text-sm">{c.name}</span>
                </button>
              ))}
            </div>
          </div>

          {/* COMENTADO: Selección de tipografía deshabilitada - Solo Titillium Web
          <div>
            <h3 className="text-sm font-medium mb-2">Tipografías</h3>
            <div className="flex flex-col">
              <label className="text-xs text-muted-foreground mb-2">Seleccioná una familia tipográfica</label>
              <select 
                value={settings.font} 
                onChange={(e) => handleSelectFont(e.target.value)} 
                className="w-full rounded-md border px-3 py-2 text-sm bg-input text-foreground"
              >
                {Object.entries(FONTS).map(([key, f]) => (
                  <option key={key} value={key}>{f.name}</option>
                ))}
                <optgroup label="Fuentes de Google">
                  {GOOGLE_FONTS.map((fam) => (
                    <option key={fam} value={`gf:${fam}`}>{fam}</option>
                  ))}
                </optgroup>
              </select>

              <div className="mt-3">
                <div className="text-sm font-medium">Previsualización</div>
                <div 
                  className="mt-2 p-3 rounded-md border" 
                  style={{ 
                    fontFamily: settings.font && settings.font.startsWith('gf:') 
                      ? settings.font.slice(3) 
                      : (FONTS[settings.font]?.family || FONTS.system.family) 
                  }}
                >
                  El rápido zorro marrón salta sobre el perro perezoso.
                </div>
              </div>
            </div>
          </div>
          */}
        </div>
      </ScrollArea>
      </div>
    </>
  );
}
